'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterBarProps {
  selectedCategory: string;
  selectedLocation: string;
  searchQuery: string;
  onCategoryChange: (category: string) => void;
  onLocationChange: (location: string) => void;
  onSearchChange: (query: string) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  selectedCategory,
  selectedLocation,
  searchQuery,
  onCategoryChange,
  onLocationChange,
  onSearchChange,
}) => {
  const categories = [
    { value: 'all', label: 'All Services', icon: 'Squares2X2Icon' },
    { value: 'health', label: 'Health Services', icon: 'HeartIcon' },
    { value: 'community', label: 'Community Support', icon: 'UserGroupIcon' },
  ];

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'sarthana', label: 'Sarthana' },
    { value: 'varachha', label: 'Varachha' },
    { value: 'kadodara', label: 'Kadodara' },
  ];

  return (
    <div className="bg-card rounded-xl shadow-subtle p-6 mb-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Search */}
        <div className="relative">
          <Icon
            name="MagnifyingGlassIcon"
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Category Filter */}
        <div className="relative">
          <Icon
            name="FunnelIcon"
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <select
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          <Icon
            name="ChevronDownIcon"
            size={20}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
        </div>

        {/* Location Filter */}
        <div className="relative">
          <Icon
            name="MapPinIcon"
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <select
            value={selectedLocation}
            onChange={(e) => onLocationChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
          >
            {locations.map((loc) => (
              <option key={loc.value} value={loc.value}>
                {loc.label}
              </option>
            ))}
          </select>
          <Icon
            name="ChevronDownIcon"
            size={20}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
        </div>
      </div>

      {/* Active Filters Display */}
      {(selectedCategory !== 'all' || selectedLocation !== 'all' || searchQuery) && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {selectedCategory !== 'all' && (
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center space-x-1">
              <span>{categories.find((c) => c.value === selectedCategory)?.label}</span>
              <button onClick={() => onCategoryChange('all')} className="hover:text-primary/80">
                <Icon name="XMarkIcon" size={14} />
              </button>
            </span>
          )}
          {selectedLocation !== 'all' && (
            <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-medium flex items-center space-x-1">
              <span>{locations.find((l) => l.value === selectedLocation)?.label}</span>
              <button onClick={() => onLocationChange('all')} className="hover:text-secondary/80">
                <Icon name="XMarkIcon" size={14} />
              </button>
            </span>
          )}
          {searchQuery && (
            <span className="px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-medium flex items-center space-x-1">
              <span>Search: {searchQuery}</span>
              <button onClick={() => onSearchChange('')} className="hover:text-accent/80">
                <Icon name="XMarkIcon" size={14} />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;